import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import type { NtaLineRow, NtaResultRow, NtaRun } from '../lib/nta';

// Network Tariff Analysis — read-only Supabase reads. RLS (migration 155) scopes
// every bv_nta_* row to the caller's org; only the server writes.

const PAGE_SIZE = 1000;
const RESULT_COLUMNS =
  'id, run_id, site_id, nmi, source, market_region, projection_from, projection_to, source_from, source_to, ' +
  'baseline_code, best_code, baseline_total, best_total, savings, baseline_not_viable, status, reasons, error, ' +
  'issued_at, accepted_at, finalised_at, created_at, ' +
  'site:site_id(id, nmi, addr_street, addr_city, client:client_id(id, name))';
const LINE_COLUMNS =
  'country, market, market_region, service_point, scenario, eligible, ineligible_reason, period_from, period_to, ' +
  'product_class, product_type, product, component, component_type, component_class, quantity, quantity_unit, ' +
  'rate_unit, rate, adjustments, adjusted_rate_unit, adjusted_rate, amount, source_from, source_to, error';

function fail<T>(error: unknown): ApiResponse<T> {
  return { success: false, error: error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error) };
}

// Writes go through the server (service role); same headers as bvReportService.
const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api/bill-validation/nta';

async function post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
        ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await res.text();
    let json: { success?: boolean; data?: unknown; error?: string };
    try { json = JSON.parse(text); } catch {
      return { success: false, error: `Request failed (${res.status}). The server did not return a valid response.` };
    }
    if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
    return { success: true, data: json.data as T };
  } catch (error: unknown) {
    return fail(error);
  }
}

export type NtaRunFilter = { siteIds?: string[]; clientId?: string; distributor?: string; state?: string; label?: string; runDate?: string };
export type NtaSliceResult = { processed: number; failed: number; status: string };

export const ntaService = {
  startRun: (filter: NtaRunFilter) => post<NtaRun>('/runs', filter),
  /** One server slice (~45 s). Call again while the returned status is 'running'. */
  work: (runId: string) => post<NtaSliceResult>(`/runs/${runId}/work`),
  pause: (runId: string) => post<{ ok: boolean }>(`/runs/${runId}/pause`),
  resume: (runId: string) => post<{ ok: boolean }>(`/runs/${runId}/resume`),
  cancel: (runId: string) => post<{ ok: boolean }>(`/runs/${runId}/cancel`),
  approve: (resultId: string) => post<{ id: string; status: string }>(`/results/${resultId}/approve`),
  issue: (resultId: string) => post<{ id: string; status: string }>(`/results/${resultId}/issue`),
  /** Issued -> accepted on `date` (YYYY-MM-DD; server defaults to today). */
  markAccepted: (resultId: string, date: string) => post<{ id: string; status: string }>(`/results/${resultId}/accept`, { date }),
  /** Accepted -> finalised on `date`. */
  markFinalised: (resultId: string, date: string) => post<{ id: string; status: string }>(`/results/${resultId}/finalise`, { date }),

  /** Distributor names as the engine prices them (bv_site_network), for the Run NTA picker. */
  listDistributors: async (): Promise<ApiResponse<string[]>> => {
    try {
      const names = new Set<string>();
      for (let page = 0; ; page++) {
        const { data, error } = await supabase.from('bv_site_network').select('distributor')
          .order('id').range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        for (const r of (data ?? []) as Array<{ distributor: string | null }>) if (r.distributor?.trim()) names.add(r.distributor.trim());
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: [...names].sort((a, b) => a.localeCompare(b)) };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  getRun: async (id: string): Promise<ApiResponse<NtaRun>> => {
    try {
      const { data, error } = await supabase.from('bv_nta_run')
        .select('id, kind, label, run_date, status, total, done, failed, created_at').eq('id', id).single();
      if (error) throw error;
      return { success: true, data: data as NtaRun };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Every result's site, status, saving and date: the Dashboard's tariff opportunities (lib/nta.tariffOpportunities). */
  listResultStatuses: async (): Promise<ApiResponse<Pick<NtaResultRow, 'site_id' | 'status' | 'savings' | 'created_at'>[]>> => {
    try {
      const all: Pick<NtaResultRow, 'site_id' | 'status' | 'savings' | 'created_at'>[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_nta_result')
          .select('site_id, status, savings, created_at')
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as Pick<NtaResultRow, 'site_id' | 'status' | 'savings' | 'created_at'>[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  listRuns: async (): Promise<ApiResponse<NtaRun[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_nta_run')
        .select('id, kind, label, run_date, status, total, done, failed, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []) as NtaRun[] };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Every result in a run, biggest saving first. */
  listResults: async (runId: string): Promise<ApiResponse<NtaResultRow[]>> => {
    try {
      const all: NtaResultRow[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_nta_result')
          .select(RESULT_COLUMNS)
          .eq('run_id', runId)
          .order('savings', { ascending: false, nullsFirst: false })
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as unknown as NtaResultRow[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  getResult: async (id: string): Promise<ApiResponse<{ result: NtaResultRow; run: NtaRun | null }>> => {
    try {
      const { data, error } = await supabase.from('bv_nta_result').select(RESULT_COLUMNS).eq('id', id).maybeSingle();
      if (error) throw error;
      if (!data) return { success: false, error: 'NTA result not found' };
      const result = data as unknown as NtaResultRow;
      const { data: run } = await supabase
        .from('bv_nta_run')
        .select('id, kind, label, run_date, status, total, done, failed, created_at')
        .eq('id', result.run_id)
        .maybeSingle();
      return { success: true, data: { result, run: (run as NtaRun | null) ?? null } };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  getLines: async (resultId: string): Promise<ApiResponse<NtaLineRow[]>> => {
    try {
      const all: NtaLineRow[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_nta_line')
          .select(LINE_COLUMNS)
          .eq('result_id', resultId)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as unknown as NtaLineRow[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** The customer's results in one run — the inputs to its client report. */
  getClientReport: async (
    runId: string, clientId: string,
  ): Promise<ApiResponse<{ client: { id: string; name: string | null }; run: NtaRun | null; results: NtaResultRow[] }>> => {
    try {
      const [clientRes, runRes, resultsRes] = await Promise.all([
        supabase.from('client').select('id, name').eq('id', clientId).maybeSingle(),
        supabase.from('bv_nta_run').select('id, kind, label, run_date, status, total, done, failed, created_at').eq('id', runId).maybeSingle(),
        supabase.from('bv_nta_result')
          .select(RESULT_COLUMNS.replace('site:site_id(', 'site:site_id!inner('))
          .eq('run_id', runId)
          .eq('site.client_id', clientId),
      ]);
      if (clientRes.error) throw clientRes.error;
      if (resultsRes.error) throw resultsRes.error;
      if (!clientRes.data) return { success: false, error: 'Client not found' };
      return {
        success: true,
        data: {
          client: clientRes.data as { id: string; name: string | null },
          run: (runRes.data as NtaRun | null) ?? null,
          results: (resultsRes.data ?? []) as unknown as NtaResultRow[],
        },
      };
    } catch (error: unknown) {
      return fail(error);
    }
  },
};
